// Thin API client. In dev CRA proxies to the backend (see package.json "proxy");
// in production IIS reverse-proxies /api to the Node app on its own port.
//
// Every call sends the session cookie. A 401 means the session expired, so we raise a
// recognisable error the app uses to bounce back to the login screen.

export class UnauthorizedError extends Error {
  constructor(message = 'Session expired') {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

async function json(res) {
  const body = await res.json().catch(() => ({}));
  if (res.status === 401) throw new UnauthorizedError(body.error || 'Session expired');
  if (!res.ok) throw new Error(body.error || `${res.status} ${res.statusText}`);
  return body;
}

// same-origin still needs to be explicit for the cookie to ride along on some setups.
const opts = (extra = {}) => ({ credentials: 'same-origin', ...extra });

const postJson = (url, body) =>
  fetch(url, opts({
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })).then(json);

// ── Auth ────────────────────────────────────────────────────────────────────────
export const login = (username, password) => postJson('/api/login', { username, password });
export const logout = () => postJson('/api/logout', {});
export const getSession = () => fetch('/api/session', opts()).then((r) => r.json());

// ── App ─────────────────────────────────────────────────────────────────────────
export const parseWorkbook = (file) => {
  const form = new FormData();
  form.append('workbook', file);
  return fetch('/api/parse', opts({ method: 'POST', body: form })).then(json);
};

export const planProject = (quote, options) => postJson('/api/plan', { quote, options });

export const searchCustomers = (q) =>
  fetch(`/api/customers?q=${encodeURIComponent(q)}`, opts()).then(json);

export const searchOwners = (q) =>
  fetch(`/api/owners?q=${encodeURIComponent(q)}`, opts()).then(json);

export const createProject = (quote, options, operationKey, retry = false) =>
  fetch('/api/create', opts({
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': operationKey,
    },
    body: JSON.stringify({ quote, options, confirm: true, retry: retry === true }),
  })).then(json);
